import "@tento-ui/components/html-editor";
import "@tento-ui/components/input/html-editor-input";
import "@tento-ui/components/icon-button";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { HtmlEditor } from "@tento-ui/components/html-editor";
import { HtmlEditorInput } from "@tento-ui/components/input/html-editor-input";
import { IconButton } from "@tento-ui/components/icon-button";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import { PencilIcon } from "@tento-ui/ui/icons";
import { ScrollShadows } from "@tento-ui/ui/scroll-shadows";

@component({
  name: "lona-event-description-container",
})
export class EventDescriptionContainer extends LonaWebComponent {
  private $wrapper: HTMLElement = this.$("description-wrapper");
  private $editable?: HtmlEditorInput;
  private $editButton?: IconButton;
  onSizeChange = this.updateScrollShadows.bind(this);

  static makeWith(
    props: EventDescriptionContainer.Props,
  ): EventDescriptionContainer {
    return EventDescriptionContainer.make().bind(props);
  }

  private handleScroll = () => {
    ScrollShadows.updateScrollShadows(this, this.$wrapper);
  };

  connectedCallback() {
    super.connectedCallback();
    this.$wrapper.addEventListener("scroll", this.handleScroll);
  }

  disconnectedCallback() {
    this.$wrapper.removeEventListener("scroll", this.handleScroll);
  }

  private updateScrollShadows(): void {
    ScrollShadows.updateScrollShadows(this, this.$wrapper);
  }

  bind(props: EventDescriptionContainer.Props): this {
    const hasContent = props.html != null && props.html !== "";

    if (!hasContent && !props.editable) {
      this.style.display = "none";
      return this;
    }

    this.style.display = "block";
    DomUtils.clearChildren(this.$wrapper);
    this.$editButton?.remove();
    this.$editButton = undefined;

    if (props.editable) {
      this.$editable = HtmlEditorInput.make();
      this.$editable.bind({
        html: props.html,
        onChange: props.onChange,
        modalTitle: props.modalTitle ?? "Update description",
      });
      this.$wrapper.appendChild(this.$editable);

      this.$editButton = IconButton.make();
      this.$editButton.id = "edit-description";
      this.$editButton.bindIcon(PencilIcon, {
        squarish: true,
        backgroundColor: "var(--bg)",
      });
      this.$editButton.addPointerEvent({
        onClick: () => this.$editable?.openHtmlEditor("event-details"),
      });
      this.shadowRoot?.appendChild(this.$editButton);
    } else {
      const $readonly = HtmlEditor.make();
      $readonly.bind({
        html: props.html ?? "",
        editable: false,
      });
      this.$wrapper.appendChild($readonly);
      this.$editable = undefined;
    }

    return this;
  }

  openHtmlEditor(context: string): void {
    this.$editable?.openHtmlEditor(context);
  }

  static $styles = [
    ScrollShadows.$styles,
    Scrollbars.$hideScrollbars,
    css`
      :host {
        display: block;
        position: relative;
        margin-top: 12px;
        margin-inline: -8px;
        border-radius: 8px;
        max-height: 300px;
        overflow: hidden;
      }

      :host(:hover) {
        background: var(--bg-hover);
      }

      #description-wrapper {
        overflow-y: auto;
        width: 100%;
        max-height: 300px;
        --editor-padding: 12px 0px;
        padding-inline: 12px;
      }

      *:focus {
        outline: none;
      }

      #edit-description {
        position: absolute;
        top: 5px;
        right: 8px;
        opacity: 0;
        box-shadow: var(--box-shadow-light);
        --std-icon-button-inset: 0px;
        z-index: 1;
        transition: opacity 0.15s ease;
      }

      :host(:hover) #edit-description {
        opacity: 1;
      }

      #edit-description svg {
        z-index: 1;
      }
    `,
  ];

  static $html = html`
    <div class="scroll-shadow-top"></div>
    <div class="scroll-shadow-bottom"></div>
    <div id="description-wrapper"></div>
  `;
}

export namespace EventDescriptionContainer {
  export type Props = {
    html: Option<string>;
    editable?: boolean;
    onChange?: (html: Option<string>) => void;
    modalTitle?: string;
  };
}
